import React, { useEffect, useMemo, useState } from "react";
import { api, fmt } from "../../../services/erpApi";
import { StatCard, PageHeader, LoadingScreen, Modal, EmptyState } from "../../../components/admin/EssUi";
import { useToast } from "../../../components/admin/ToastContext";
import { Search, SlidersHorizontal, ArrowDownToLine, ArrowLeftRight, Plus, X, Package, Factory, Banknote, AlertTriangle } from "lucide-react";

const PAGE_SIZE = 15;

const AdminPanelStockPage = () => {
  const toast = useToast();
  const [data, setData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [warehouseFilter, setWarehouseFilter] = useState("all");
  const [page, setPage] = useState(1);

  const [warehouses, setWarehouses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);
  const [partners, setPartners] = useState([]);
  const [showAdjust, setShowAdjust] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [form, setForm] = useState({ product_id: "", location_id: "", quantity: "", reason: "" });
  const [showReceive, setShowReceive] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const [receiveForm, setReceiveForm] = useState({ warehouse_id: "", dest_location_id: "", partner_id: "", date: new Date().toISOString().slice(0, 10), reference: "", lines: [{}] });
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [transferForm, setTransferForm] = useState({ source_location_id: "", dest_location_id: "", date: new Date().toISOString().slice(0, 10), lines: [{}] });
  const [showRevalue, setShowRevalue] = useState(false);
  const [revalueTarget, setRevalueTarget] = useState(null);
  const [revalueCost, setRevalueCost] = useState("");
  const [revaluing, setRevaluing] = useState(false);

  useEffect(() => { setPage(1); }, [search, warehouseFilter]);

  const load = async () => {
    try {
      const rows = await api.inventory.stock({ limit: 500 });
      setData(rows || []);
    } catch { setData([]); }
  };

  useEffect(() => {
    (async () => {
      await Promise.all([
        load(),
        api.inventory.warehouses().catch(() => []),
        api.inventory.locations({}).catch(() => []),
        api.products.list({ limit: 500, active: true }).catch(() => []),
        api.partners.list({ limit: 500 }).catch(() => []),
      ]).then(([, w, l, p, pt]) => {
        setWarehouses(w || []);
        setLocations(l || []);
        setProducts(p || []);
        setPartners(pt || []);
      });
      setLoaded(true);
    })();
  }, []);

  const openAdjust = async () => {
    const [l] = await Promise.all([api.inventory.locations({ usage: "internal" }).catch(() => [])]);
    setLocations(l || []);
    setForm({ product_id: "", location_id: "", quantity: "", reason: "" });
    setShowAdjust(true);
  };

  const handleAdjust = async () => {
    if (!form.product_id) { toast("Product is required", "error"); return; }
    if (form.quantity === "") { toast("Target quantity is required", "error"); return; }
    setAdjusting(true);
    try {
      const body = {
        product_id: parseInt(form.product_id),
        location_id: form.location_id ? parseInt(form.location_id) : undefined,
        quantity: parseFloat(form.quantity),
        reason: form.reason || "Manual adjustment",
      };
      await api.inventory.adjustments(body);
      toast("Stock adjusted", "success");
      setShowAdjust(false);
      await load();
    } catch (e) {
      toast(e.message || "Adjustment failed", "error");
    }
    setAdjusting(false);
  };

  const openReceive = () => {
    setReceiveForm({ warehouse_id: "", dest_location_id: "", partner_id: "", date: new Date().toISOString().slice(0, 10), reference: "", lines: [{}] });
    setShowReceive(true);
  };

  const handleLineChange = (form, setForm, idx, patch) => {
    setForm((f) => {
      const lines = f.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l));
      return { ...f, lines };
    });
  };

  const addLine = (setForm) => setForm((f) => ({ ...f, lines: [...f.lines, {}] }));
  const removeLine = (setForm, idx) => setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));

  const handleReceive = async () => {
    const validLines = receiveForm.lines.filter((l) => l.product_id && l.quantity);
    if (validLines.length === 0) { toast("Add at least one product line", "error"); return; }
    setReceiving(true);
    try {
      const body = {
        warehouse_id: receiveForm.warehouse_id ? parseInt(receiveForm.warehouse_id) : undefined,
        dest_location_id: receiveForm.dest_location_id ? parseInt(receiveForm.dest_location_id) : undefined,
        partner_id: receiveForm.partner_id ? parseInt(receiveForm.partner_id) : undefined,
        date: receiveForm.date || undefined,
        reference: receiveForm.reference || undefined,
        lines: validLines.map((l) => ({ product_id: parseInt(l.product_id), quantity: parseFloat(l.quantity), ...(l.unit_cost !== "" ? { unit_cost: parseFloat(l.unit_cost) } : {}) })),
      };
      const res = await api.inventory.receive(body);
      toast(res?.picking_reference ? `Received · ${res.picking_reference}` : "Goods received", "success");
      setShowReceive(false);
      await load();
    } catch (e) {
      toast(e.message || "Reception failed", "error");
    }
    setReceiving(false);
  };

  const openTransfer = () => {
    setTransferForm({ source_location_id: "", dest_location_id: "", date: new Date().toISOString().slice(0, 10), lines: [{}] });
    setShowTransfer(true);
  };

  const handleTransfer = async () => {
    const validLines = transferForm.lines.filter((l) => l.product_id && l.quantity);
    if (validLines.length === 0) { toast("Add at least one product line", "error"); return; }
    if (!transferForm.source_location_id) { toast("Source location is required", "error"); return; }
    if (!transferForm.dest_location_id) { toast("Destination location is required", "error"); return; }
    setTransferring(true);
    try {
      const body = {
        source_location_id: parseInt(transferForm.source_location_id),
        dest_location_id: parseInt(transferForm.dest_location_id),
        date: transferForm.date || undefined,
        lines: validLines.map((l) => ({ product_id: parseInt(l.product_id), quantity: parseFloat(l.quantity) })),
      };
      await api.inventory.transfer(body);
      toast("Transfer complete", "success");
      setShowTransfer(false);
      await load();
    } catch (e) {
      toast(e.message || "Transfer failed", "error");
    }
    setTransferring(false);
  };

  const openRevalue = (row) => {
    setRevalueTarget(row);
    setRevalueCost(String(Number(row.purchase_price ?? row.standard_price) || ""));
    setShowRevalue(true);
  };

  const handleRevalue = async () => {
    if (!revalueTarget) return;
    const unitCost = Number(revalueCost);
    if (!unitCost || unitCost <= 0) { toast("Unit cost must be greater than 0", "error"); return; }
    setRevaluing(true);
    try {
      const res = await api.inventory.revalue(revalueTarget.id, { unit_cost: unitCost });
      toast(res?.new_unit_cost ? `Revalued · ${fmt.money(res.new_unit_cost)}` : "Stock revalued", "success");
      setShowRevalue(false);
      setRevalueTarget(null);
      await load();
    } catch (e) {
      toast(e.message || "Revaluation failed", "error");
    }
    setRevaluing(false);
  };

  const stats = useMemo(() => {
    const totalQty = data.reduce((s, r) => s + (Number(r.qty_on_hand) || 0), 0);
    const stockValue = data.reduce((s, r) => s + (Number(r.qty_on_hand) || 0) * (Number(r.purchase_price ?? r.standard_price) || 0), 0);
    const lowStock = data.filter((r) => (Number(r.qty_available) || 0) < 5).length;
    return { count: data.length, totalQty, stockValue, lowStock };
  }, [data]);

  const filtered = useMemo(() => {
    return data.filter((r) => {
      if (warehouseFilter !== "all" && String(r.warehouse_id) !== warehouseFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const hay = `${r.name || ""} ${r.default_code || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data, search, warehouseFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const inputStyle = {
    width: "100%", padding: "9px 12px", border: "1px solid #cbd5e1", borderRadius: "6px",
    fontSize: "13.5px", outline: "none", background: "#fff", boxSizing: "border-box",
  };
  const labelStyle = { fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "6px", display: "block" };
  const FormField = ({ label, children }) => (
    <div style={{ marginBottom: "16px" }}>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );

  if (!loaded) return <LoadingScreen />;

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: "0 16px" }}>
      <PageHeader
        title="Stock"
        subtitle="Inventory quantities on hand, reserved and available"
        actions={
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <button onClick={openReceive} className="ess-btn ess-btn-primary">
              <ArrowDownToLine size={16} />
              Receive
            </button>
            <button onClick={openTransfer} className="ess-btn ess-btn-primary">
              <ArrowLeftRight size={16} />
              Transfer
            </button>
            <button onClick={openAdjust} className="ess-btn ess-btn-primary">
              <SlidersHorizontal size={16} />
              Adjust Stock
            </button>
          </div>
        }
      />

      <div className="ess-sg ess-sg4" style={{ marginBottom: "24px" }}>
        <StatCard label="Products in Stock" value={String(stats.count)} icon={Package} color="indigo" trend="Items tracked" />
        <StatCard label="Units on Hand" value={fmt.qty(stats.totalQty)} icon={Factory} color="blue" trend="Total quantity" />
        <StatCard label="Stock Value" value={fmt.moneyShort(stats.stockValue)} icon={Banknote} color="green" trend={fmt.money(stats.stockValue)} />
        <StatCard label="Low Stock" value={String(stats.lowStock)} icon={AlertTriangle} color="rose" trend="Available &lt; 5 units" />
      </div>

      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "24px", background: "white", padding: "16px", borderRadius: "8px", border: "1px solid #e2e7e2" }}>
        <div style={{ flex: 1, minWidth: "200px", position: "relative" }}>
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#94a3b8" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search product or code..." style={{ ...inputStyle, padding: "10px 12px 10px 36px" }} />
        </div>
        <select value={warehouseFilter} onChange={(e) => setWarehouseFilter(e.target.value)} style={{ ...inputStyle, minWidth: "180px", cursor: "pointer" }}>
          <option value="all">All Warehouses</option>
          {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
      </div>

      <div style={{ background: "white", borderRadius: "8px", border: "1px solid #e2e7e2", overflow: "hidden" }}>
        {paginated.length === 0 ? (
          <EmptyState icon="📦" title="No stock" subtitle="Stock levels will appear once you receive goods" />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <thead>
                <tr>
                  <th className="ess-th" style={{ minWidth: "180px" }}>Product</th>
                  <th className="ess-th" style={{ minWidth: "90px" }}>Code</th>
                  <th className="ess-th" style={{ textAlign: "center", minWidth: "90px" }}>On Hand</th>
                  <th className="ess-th" style={{ textAlign: "center", minWidth: "90px" }}>Reserved</th>
                  <th className="ess-th" style={{ textAlign: "center", minWidth: "100px" }}>Available</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "110px" }}>Cost</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "110px" }}>Sale Price</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "100px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((row) => {
                  const avail = Number(row.qty_available) || 0;
                  const availColor = avail <= 0 ? { text: "#be123c", bg: "#fff1f2" } : avail < 5 ? { text: "#b45309", bg: "#fffbeb" } : { text: "#225f2b", bg: "#f0f7f0" };
                  return (
                    <tr key={row.id} className="ess-tr">
                      <td className="ess-td" style={{ fontWeight: "500", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.name || "—"}</td>
                      <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{row.default_code || "—"}</td>
                      <td className="ess-td" style={{ fontWeight: "600", textAlign: "center", whiteSpace: "nowrap" }}>{fmt.qty(row.qty_on_hand)}</td>
                      <td className="ess-td" style={{ color: "#475569", textAlign: "center", whiteSpace: "nowrap" }}>{fmt.qty(row.qty_reserved)}</td>
                      <td className="ess-td" style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                        <span style={{ background: availColor.bg, color: availColor.text, padding: "3px 10px", borderRadius: "6px", fontSize: "12px", fontWeight: "600", whiteSpace: "nowrap" }}>{fmt.qty(avail)}</span>
                      </td>
                      <td className="ess-td" style={{ color: "#475569", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(row.purchase_price ?? row.standard_price)}</td>
                      <td className="ess-td" style={{ fontWeight: "600", color: "#225f2b", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(row.sales_price ?? row.list_price)}</td>
                      <td className="ess-td" style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        <button title="Revalue unit cost" onClick={() => openRevalue(row)} className="ess-btn ess-btn-secondary" style={{ color: "#b45309" }}>
                          Revalue
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderTop: "1px solid #e2e7e2", background: "#fafbfa" }}>
          <span style={{ fontSize: "13px", color: "#64748b" }}>
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div style={{ display: "flex", gap: "4px" }}>
            <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: page <= 1 ? "#f1f5f9" : "white", color: page <= 1 ? "#94a3b8" : "#334155", fontSize: "13px", fontWeight: "500", cursor: page <= 1 ? "not-allowed" : "pointer" }}>← Prev</button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const start = Math.max(1, Math.min(page - 2, totalPages - 4));
              const p = start + i;
              if (p > totalPages) return null;
              return <button key={p} onClick={() => setPage(p)} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: p === page ? "#225f2b" : "white", color: p === page ? "white" : "#334155", fontSize: "13px", fontWeight: p === page ? "600" : "500", cursor: "pointer" }}>{p}</button>;
            })}
            <button disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: page >= totalPages ? "#f1f5f9" : "white", color: page >= totalPages ? "#94a3b8" : "#334155", fontSize: "13px", fontWeight: "500", cursor: page >= totalPages ? "not-allowed" : "pointer" }}>Next →</button>
          </div>
        </div>
      </div>

      <Modal open={showAdjust} onClose={() => setShowAdjust(false)} title="Adjust Stock" size="md">
        <div>
          <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "16px" }}>
            Set the target quantity. The difference from the current stock is applied as a stock movement (adjustment).
          </p>
          <FormField label="Product *">
            <select value={form.product_id} onChange={(e) => setForm((f) => ({ ...f, product_id: e.target.value }))} style={inputStyle}>
              <option value="">Select product…</option>
              {data.map((p) => <option key={p.id} value={p.id}>{p.name}{p.default_code ? ` (${p.default_code})` : ""} — on hand: {fmt.qty(p.qty_on_hand)}</option>)}
            </select>
          </FormField>
          <FormField label="Location">
            <select value={form.location_id} onChange={(e) => setForm((f) => ({ ...f, location_id: e.target.value }))} style={inputStyle}>
              <option value="">Default location…</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </FormField>
          <FormField label="Target Quantity *">
            <input type="number" min="0" value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))} placeholder="0" style={inputStyle} />
          </FormField>
          <FormField label="Reason">
            <input value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} placeholder="Physical inventory" style={inputStyle} />
          </FormField>
          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: "20px" }}>
            <button onClick={() => setShowAdjust(false)} className="ess-btn ess-btn-secondary">Cancel</button>
            <button onClick={handleAdjust} disabled={adjusting} className="ess-btn ess-btn-primary">
              {adjusting ? "Applying…" : "Apply Adjustment"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={showReceive} onClose={() => setShowReceive(false)} title="Receive Goods" size="lg">
        <div>
          <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "16px" }}>
            Create a direct goods reception (stock in) without a purchase order.
          </p>
          <FormField label="Warehouse">
            <select value={receiveForm.warehouse_id} onChange={(e) => setReceiveForm((f) => ({ ...f, warehouse_id: e.target.value }))} style={inputStyle}>
              <option value="">Select warehouse…</option>
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </FormField>
          <FormField label="Destination Location">
            <select value={receiveForm.dest_location_id} onChange={(e) => setReceiveForm((f) => ({ ...f, dest_location_id: e.target.value }))} style={inputStyle}>
              <option value="">Default location…</option>
              {locations.filter((l) => l.usage === "internal").map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </FormField>
          <div style={{ display: "grid", gap: "12px" }} className="ess-rg-2">
            <FormField label="Supplier">
              <select value={receiveForm.partner_id} onChange={(e) => setReceiveForm((f) => ({ ...f, partner_id: e.target.value }))} style={inputStyle}>
                <option value="">No supplier…</option>
                {partners.map((p) => <option key={p.id} value={p.id}>{p.display_name || p.name}</option>)}
              </select>
            </FormField>
            <FormField label="Date">
              <input type="date" value={receiveForm.date} onChange={(e) => setReceiveForm((f) => ({ ...f, date: e.target.value }))} style={inputStyle} />
            </FormField>
          </div>
          <FormField label="Reference">
            <input value={receiveForm.reference} onChange={(e) => setReceiveForm((f) => ({ ...f, reference: e.target.value }))} placeholder="RECV/2026/00001 (auto if empty)" style={inputStyle} />
          </FormField>

          <label style={labelStyle}>Products</label>
          <div style={{ border: "1px solid #e2e7e2", borderRadius: "8px", padding: "12px", marginBottom: "16px" }} className="ess-lines">
            {receiveForm.lines.map((line, idx) => (
              <div key={idx} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 36px", gap: "8px", alignItems: "center", marginBottom: "8px" }} className="ess-line">
                <select value={line.product_id || ""} onChange={(e) => handleLineChange(receiveForm, setReceiveForm, idx, { product_id: e.target.value })} style={inputStyle}>
                  <option value="">Select product…</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}{p.default_code ? ` (${p.default_code})` : ""}</option>)}
                </select>
                <input type="number" min="0" value={line.quantity ?? ""} onChange={(e) => handleLineChange(receiveForm, setReceiveForm, idx, { quantity: e.target.value })} placeholder="Qty" style={inputStyle} />
                <input type="number" min="0" value={line.unit_cost ?? ""} onChange={(e) => handleLineChange(receiveForm, setReceiveForm, idx, { unit_cost: e.target.value })} placeholder="Unit cost" style={inputStyle} />
                <button onClick={() => removeLine(setReceiveForm, idx)} disabled={receiveForm.lines.length === 1} style={{ background: "transparent", border: "none", color: "#be123c", cursor: receiveForm.lines.length === 1 ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <X size={16} />
                </button>
              </div>
            ))}
            <button onClick={() => addLine(setReceiveForm)} className="inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: "#225f2b", background: "none", border: "none", cursor: "pointer", padding: "4px 0" }}>
              <Plus size={16} /> Add product line
            </button>
          </div>

          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: "20px" }}>
            <button onClick={() => setShowReceive(false)} className="ess-btn ess-btn-secondary">Cancel</button>
            <button onClick={handleReceive} disabled={receiving} className="ess-btn ess-btn-primary">
              {receiving ? "Receiving…" : "Receive Goods"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={showRevalue} onClose={() => setShowRevalue(false)} title={`Revalue Unit Cost — ${revalueTarget?.name || ""}`} size="md">
        <div>
          <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "16px" }}>
            Update the unit cost of this product. A revaluation stock movement is recorded for traceability and the stock value is recalculated.
          </p>
          <FormField label="New Unit Cost *">
            <input type="number" min="0" value={revalueCost} onChange={(e) => setRevalueCost(e.target.value)} placeholder="3500" style={inputStyle} />
          </FormField>
          <div style={{ fontSize: "13px", color: "#475569", marginBottom: "16px" }}>
            Current on hand: <strong>{fmt.qty(revalueTarget?.qty_on_hand)}</strong> · Projected value: <strong>{fmt.money((Number(revalueTarget?.qty_on_hand) || 0) * (Number(revalueCost) || 0))}</strong>
          </div>
          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: "20px" }}>
            <button onClick={() => setShowRevalue(false)} className="ess-btn ess-btn-secondary">Cancel</button>
            <button onClick={handleRevalue} disabled={revaluing} className="ess-btn ess-btn-primary">
              {revaluing ? "Revaluing…" : "Revalue Cost"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={showTransfer} onClose={() => setShowTransfer(false)} title="Transfer Stock" size="lg">
        <div>
          <p style={{ fontSize: "13px", color: "#64748b", marginBottom: "16px" }}>
            Move stock between two internal locations or warehouses.
          </p>
          <div style={{ display: "grid", gap: "12px" }} className="ess-rg-2">
            <FormField label="Source Location *">
              <select value={transferForm.source_location_id} onChange={(e) => setTransferForm((f) => ({ ...f, source_location_id: e.target.value }))} style={inputStyle}>
                <option value="">Select source…</option>
                {locations.filter((l) => l.usage === "internal").map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </FormField>
            <FormField label="Destination Location *">
              <select value={transferForm.dest_location_id} onChange={(e) => setTransferForm((f) => ({ ...f, dest_location_id: e.target.value }))} style={inputStyle}>
                <option value="">Select destination…</option>
                {locations.filter((l) => l.usage === "internal").map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </FormField>
          </div>
          <FormField label="Date">
            <input type="date" value={transferForm.date} onChange={(e) => setTransferForm((f) => ({ ...f, date: e.target.value }))} style={inputStyle} />
          </FormField>

          <label style={labelStyle}>Products</label>
          <div style={{ border: "1px solid #e2e7e2", borderRadius: "8px", padding: "12px", marginBottom: "16px" }} className="ess-lines">
            {transferForm.lines.map((line, idx) => (
              <div key={idx} style={{ display: "grid", gridTemplateColumns: "3fr 1fr 36px", gap: "8px", alignItems: "center", marginBottom: "8px" }} className="ess-line">
                <select value={line.product_id || ""} onChange={(e) => handleLineChange(transferForm, setTransferForm, idx, { product_id: e.target.value })} style={inputStyle}>
                  <option value="">Select product…</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}{p.default_code ? ` (${p.default_code})` : ""}</option>)}
                </select>
                <input type="number" min="0" value={line.quantity ?? ""} onChange={(e) => handleLineChange(transferForm, setTransferForm, idx, { quantity: e.target.value })} placeholder="Qty" style={inputStyle} />
                <button onClick={() => removeLine(setTransferForm, idx)} disabled={transferForm.lines.length === 1} style={{ background: "transparent", border: "none", color: "#be123c", cursor: transferForm.lines.length === 1 ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <X size={16} />
                </button>
              </div>
            ))}
            <button onClick={() => addLine(setTransferForm)} className="inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: "#225f2b", background: "none", border: "none", cursor: "pointer", padding: "4px 0" }}>
              <Plus size={16} /> Add product line
            </button>
          </div>

          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", borderTop: "1px solid #e2e7e2", paddingTop: "20px" }}>
            <button onClick={() => setShowTransfer(false)} className="ess-btn ess-btn-secondary">Cancel</button>
            <button onClick={handleTransfer} disabled={transferring} className="ess-btn ess-btn-primary">
              {transferring ? "Transferring…" : "Apply Transfer"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AdminPanelStockPage;
